import React, { useEffect, useMemo, useState } from "react";
import { ButtonOnlyTitle } from "../../Buttons/ButtonOnlyTitle/Index";
import { useEditOnboarding } from "../../../hooks/useEditOnboarding";
import axios from "../../../services/axios";
import InventoryTable from "./table";
import { IAreas } from "../../../@types/API/IArea";
import { FormProvider, useForm } from "react-hook-form";
import { Modal } from "../../Modal/Default/Index";
import { InventoryTaskStop } from "../../Modal/InventoryStopTasks";
import { useTranslation } from "react-i18next";

interface FormValues {
  [batchIndex: string]: {
    [categoryId: string]: number | string | undefined;
  }
}

interface InventoryChange {
  batch_id: number;
  category_id: number;
  quantity: number;
  id?: number;
}

export const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const { setOptionForm, farmIdSelected, cardInfo, idSelected, } = useEditOnboarding();
  const [areaIds, setAreaIds] = useState<number[]>();
  const [inventory, setInventory] = useState<IBatchCategoryContent[]>();
  const [categoryAdjustments, setCategoryAdjustments] = useState<Record<number, number>>({});
  const [currentInventory, setCurrentInventory] = useState<IBatchCategoryContent[]>([]);
  const [completeTaskModal, setCompleteTaskModal] = useState(false);
  const [error, setError] = useState(false);
  const methods = useForm()
  const adjustmentIdsByCell = useMemo(() => new Map<string, number>(), [idSelected]);

  useEffect(() => {
    if (inventory) {
      methods.setValue('batches',
        inventory ?
          inventory.reduce((acc, batch) => {
            acc[batch.batch_id] = {};
            batch.categories.forEach((cat) => {
              acc[batch.batch_id][cat.id] = cat.quantity;
            });
            return acc;
          }, {} as FormValues)
          : undefined
      )
    }
  }, [inventory]);

  useEffect(() => {
    if (currentInventory) {
      methods.setValue('inventory',
        currentInventory ?
          currentInventory.reduce((acc, batch) => {
            acc[batch.batch_id] = {};
            batch.categories.forEach((cat) => {
              acc[batch.batch_id][cat.id] = cat.quantity;
            });
            return acc;
          }, {} as FormValues)
          : undefined
      )
    }
  }, [currentInventory]);

  useEffect(() => {
    axios
      .get(`/areas`, { params: { farm_id: farmIdSelected } })
      .then(({ data }) => {
        const areas: IAreas = data
        setAreaIds(areas.map(area => Number(area.id)))
      });
  }, []);

  useEffect(() => {
    setCategoryAdjustments({});

    if (farmIdSelected && areaIds && areaIds.length > 0) {
      axios
        .get(`/batches/categories?farmId=${farmIdSelected}&areaIds=${cardInfo.area_id}&size=${999}`)
        .then((response) => {
          const batchInventory = [...(response.data.content as IBatchCategoryContent[])]
            .sort((a, b) => a.initials.localeCompare(b.initials));
          const adjustments = batchInventory.reduce((totals, batch) => {
            batch.categories.forEach(category => {
              totals[category.id] = (totals[category.id] ?? 0) + (category.inventory_adjustment ?? 0);
            });
            return totals;
          }, {} as Record<number, number>);
          setCategoryAdjustments(adjustments);

          if (cardInfo.state === "COMPLETED") {
            axios.get(`/batch-category-history?taskId=${cardInfo.id}`)
              .then((response) => {
                const responseHistory = response.data as IInventoryFix[];

                const adjustmentsMap = new Map<number, number>();
                adjustmentsMap.clear();

                axios.get(`/area-inventory-adjustment-history?taskId=${cardInfo.id}`)
                  .then((response) => {
                    const responseAdjustments = response.data as IAdjustmentsInventoryHistory[];

                    responseAdjustments.forEach(item => {
                      adjustmentsMap.set(item.category_id, item.balance);
                    });
                    setCategoryAdjustments(Object.fromEntries(adjustmentsMap));
                  })

                const historyMap = new Map<string, IInventoryFix>();
                historyMap.clear();

                responseHistory.forEach(item => {
                  historyMap.set(`${item.batch_id}.${item.category_id}`, item);
                })

                const historyInventory = batchInventory.map((batch: IBatchCategoryContent) => {
                  return {
                    ...batch,
                    categories: Array.from(historyMap.values())
                      .filter(value => value.batch_id === batch.batch_id)
                      .map(value => ({
                        id: value.category_id,
                        quantity: value.quantity,
                        age_description: "",
                        inventory_adjustment: 0,
                        month_reference: 0,
                        name: "",
                        unit: ""
                      } as unknown as Category))
                  };
                });

                const allCategoriesEmpty = historyInventory.every(batch => batch.categories.length === 0);

                setInventory(allCategoriesEmpty ? batchInventory : historyInventory);
              })
              .catch((error) => {
                setInventory(batchInventory);
              });
          } else {
            setInventory(batchInventory);
          }
        });
    }
  }, [areaIds]);

  useEffect(() => {
    let cancelled = false;
    adjustmentIdsByCell.clear();
    if (inventory && idSelected && cardInfo.date &&
      (cardInfo.state === "DRAFT" || cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED")) {
      axios.get(`/tasks/batch-inventory-adjustment/${idSelected}`)
        .then((response) => {
          if (cancelled) return;
          const responseAdjustment = response.data as IInventoryFix[];
          const adjustmentMap = new Map<string, IInventoryFix>();
          responseAdjustment.forEach(item => {
            adjustmentMap.set(`${item.batch_id}.${item.category_id}`, item);
            adjustmentIdsByCell.set(`${item.batch_id}.${item.category_id}`, item.id);
          });

          const updatedInventory = inventory.map(batch => {
            return {
              ...batch,
              categories: [
                ...batch.categories.map(category => {
                  const adjustment = adjustmentMap.get(`${batch.batch_id}.${category.id}`);
                  return adjustment ? { ...category, quantity: adjustment.quantity } : category;
                }),
                ...Array.from(adjustmentMap.values())
                  .filter(value => value.batch_id === batch.batch_id && !batch.categories.some(category => category.id === value.category_id))
                  .map(value => ({
                    id: value.category_id,
                    quantity: value.quantity,
                    age_description: "",
                    inventory_adjustment: 0,
                    month_reference: 0,
                    name: "",
                    unit: ""
                  } as unknown as Category))
              ]
            };
          });

          if (cardInfo.state === "COMPLETED") {
            setCurrentInventory(inventory)
          } else if (cardInfo.state === "DRAFT") {
            setCurrentInventory(updatedInventory)
          } else {
            setCurrentInventory(updatedInventory)
          }
        })
        .catch(() => {
          if (!cancelled) setCurrentInventory(inventory);
        })
    } else if (inventory) {
      setCurrentInventory(inventory)
    }
    return () => { cancelled = true; };
  }, [inventory, idSelected, cardInfo.state, cardInfo.date, adjustmentIdsByCell]);

  const getInventoryArray = (data: { inventory?: FormValues }): InventoryChange[] => {
    const referenceQuantities = new Map<string, number>();
    inventory?.forEach(batch => batch.categories.forEach(category => {
      referenceQuantities.set(
        `${batch.batch_id}.${category.id}`,
        category.quantity - (category.inventory_adjustment ?? 0),
      );
    }));

    return Object.entries(data.inventory || {}).flatMap(([batchId, categories]) =>
      Object.entries(categories).flatMap(([categoryId, value]) => {
        if (value === undefined || value === "") return [];
        const quantity = Number(value);
        const cellKey = `${batchId}.${categoryId}`;
        if (!Number.isFinite(quantity) || quantity === (referenceQuantities.get(cellKey) ?? 0)) return [];

        const change: InventoryChange = {
          batch_id: Number(batchId),
          category_id: Number(categoryId),
          quantity,
        };
        const adjustmentId = adjustmentIdsByCell.get(cellKey);
        if (idSelected && adjustmentId !== undefined) {
          change.id = adjustmentId;
        }
        return [change];
      })
    );
  };

  const postInventory = async (
    inventoryArray: InventoryChange[],
    state: "DRAFT" | "PENDING"
  ) => {
    if (!areaIds || areaIds.length === 0) return;
    const body = {
      type: "BATCH_INVENTORY_ADJUSTMENT",
      state,
      changes: inventoryArray,
      draft_task_id: idSelected,
      area_id: cardInfo.area_id
    };

    axios.post("/tasks", body)
      .then(() => setError(false))
      .catch(err => {
        console.error(err)
        setError(true)
      }).finally(() => {
        if (state == "PENDING")
          setCompleteTaskModal(true);
        else setOptionForm(undefined)
      })
  };

  function handleCompleteInventory(data: any) {
    if (!areaIds || areaIds.length === 0) return;
    const inventoryArray = getInventoryArray(data);

    postInventory(inventoryArray, "PENDING");
  }

  function handleDraftInventory(data: any) {
    if (!areaIds || areaIds.length === 0) return;
    const inventoryArray = getInventoryArray(data);

    postInventory(inventoryArray, "DRAFT");
  }

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <div
            style={{
              overflowY: "scroll",
              height: "calc(100vh - 340px)",
            }}
            id="formInventory"
          >
            <>
              {inventory && (
                <>
                  <h2>{t("inventory.currentBatches")}</h2>
                  <InventoryTable
                    dataTable={inventory}
                    onlyRead
                    typeTable={'batches'}
                    categoryAdjustments={categoryAdjustments}
                    methods={methods as any}
                  />
                </>
              )}
              <br />
              {currentInventory && inventory && (
                <>
                  <h2>{t("inventory.correction")}</h2>
                  <InventoryTable
                    dataTable={currentInventory}
                    referenceTable={inventory}
                    typeTable={'inventory'}
                    methods={methods as any}
                    onlyRead={cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED"}
                    viewAdjustment
                  />
                </>
              )}
            </>
          </div>

          <div className="footer__EditOnboarding">
            <div className="footer__formEditOnboarding">
              <ButtonOnlyTitle title={t("editFooter.back")} theme="light" onClick={() => setOptionForm(undefined)} />
              <ButtonOnlyTitle title={t("editFooter.save")} theme="light" onClick={methods.handleSubmit(data => handleDraftInventory(data))} disabled={cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED"} />
              <ButtonOnlyTitle title={t("editFooter.complete")} theme="info" disabled={(cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED") || completeTaskModal} onClick={methods.handleSubmit(data => handleCompleteInventory(data))} />
            </div>
          </div>
        </form>
      </FormProvider>

      <Modal
        visible={completeTaskModal}
        contentStyle={{
          padding: "60px",
          width: "433px",
          height: "auto",
          borderRadius: 25,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 30
        }}
      >
        <span>
          {error ? t("inventory.actionError") : t("inventory.fieldTaskRequired")}
        </span>
        <footer className="buttons-container">
          <ButtonOnlyTitle
            title={t("inventory.complete")}
            theme="info"
            onClick={() => {
              setCompleteTaskModal(false)
              if (!error)
                setOptionForm(undefined)
            }}
          />
        </footer>
      </Modal>

      <InventoryTaskStop />
    </>
  );
};
